import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('apex_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth API
export const authAPI = {
  async register(email, password, name, finnhubKey = "") {
    const response = await axios.post(`${API}/auth/register`, {
      email, password, name, finnhub_key: finnhubKey
    });
    return response.data;
  },

  async login(email, password) {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    return response.data;
  },

  async getMe() {
    const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
    return response.data;
  },

  async updateProfile(updates) {
    const response = await axios.put(`${API}/auth/profile`, updates, { headers: getAuthHeaders() });
    return response.data;
  }
};

// Finnhub API calls via backend proxy
export const finnhubAPI = {
  async getQuote(ticker, apiKey) {
    try {
      const response = await axios.post(`${API}/finnhub/quote`, {
        ticker,
        api_key: apiKey
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching quote for ${ticker}:`, error);
      return null;
    }
  },

  async getCandles(ticker, apiKey, days = 30) {
    try {
      const response = await axios.post(`${API}/finnhub/candles`, {
        ticker,
        api_key: apiKey,
        resolution: "D",
        days
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching candles for ${ticker}:`, error);
      return null;
    }
  },

  async getNews(apiKey) {
    try {
      const response = await axios.post(`${API}/finnhub/news`, {
        ticker: "",
        api_key: apiKey
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching news:', error);
      return [];
    }
  },

  async getATHBatch(tickers) {
    try {
      const response = await axios.post(`${API}/ath/batch`, { tickers });
      return response.data;
    } catch (error) {
      console.error('Error fetching ATH:', error);
      return {};
    }
  },

  async getYFinanceBatchQuotes(tickers, portfolio = false) {
    try {
      const response = await axios.post(`${API}/quotes/yfinance-batch`, { tickers, portfolio }, { timeout: 45000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching yfinance quotes:', error);
      return {};
    }
  },

  async searchStocks(query) {
    try {
      const response = await axios.get(`${API}/search/stocks`, { params: { q: query }, timeout: 5000 });
      return response.data.results || [];
    } catch (error) {
      console.error('Error searching stocks:', error);
      return [];
    }
  },

  async getStockPerformance(ticker) {
    try {
      const response = await axios.get(`${API}/stock/performance`, { params: { ticker }, timeout: 15000 });
      return response.data.performance || {};
    } catch (error) {
      console.error('Error fetching stock performance:', error);
      return {};
    }
  },

  async getStockFundamentals(ticker) {
    try {
      const response = await axios.get(`${API}/stock/fundamentals`, { params: { ticker }, timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching stock fundamentals:', error);
      return { kpis: {} };
    }
  },

  async getFundamentalsPro(ticker) {
    try {
      const response = await axios.get(`${API}/stock/fundamentals-pro`, { params: { ticker }, timeout: 30000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching pro fundamentals:', error);
      return null;
    }
  },

  async getFinancialsHistory(ticker) {
    try {
      const response = await axios.get(`${API}/stock/financials-history`, { params: { ticker }, headers: getAuthHeaders(), timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching financials history:', error);
      return { history: [] };
    }
  },

  async getInsiderActivity(ticker) {
    try {
      const response = await axios.get(`${API}/stock/insiders`, { params: { ticker }, headers: getAuthHeaders(), timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching insider activity:', error);
      return { transactions: [], summary: {} };
    }
  },

  async getAnalystSnapshot(ticker) {
    try {
      const response = await axios.get(`${API}/stock/analyst-snapshot`, { params: { ticker }, timeout: 20000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching analyst snapshot:', error);
      return null;
    }
  },

  async getSparklinesBatch(tickers) {
    try {
      const response = await axios.post(`${API}/sparklines/batch`, { tickers }, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching sparklines:', error);
      return {};
    }
  },

  async getPrePostMarketBatch(tickers) {
    try {
      const response = await axios.post(`${API}/pre-post-market/batch`, { tickers }, { timeout: 45000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching pre/post market:', error);
      return {};
    }
  },

  async getEarningsBatch(tickers) {
    try {
      const response = await axios.post(`${API}/earnings/batch`, { tickers });
      return response.data;
    } catch (error) {
      console.error('Error fetching earnings:', error);
      return {};
    }
  },

  async getSupportResistanceBatch(tickers) {
    try {
      const response = await axios.post(`${API}/support-resistance/batch`, { tickers }, { timeout: 30000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching support/resistance:', error);
      return {};
    }
  },

  async getValuationBatch(tickers) {
    try {
      const response = await axios.post(`${API}/valuation/batch`, { tickers }, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching valuation:', error);
      return {};
    }
  },

  async getValuationDetail(ticker) {
    try {
      const response = await axios.post(`${API}/valuation/detail`, { ticker });
      return response.data;
    } catch (error) {
      console.error('Error fetching valuation detail:', error);
      return {};
    }
  },

  async getEconomicCalendar() {
    try {
      const response = await axios.get(`${API}/economic-calendar`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching economic calendar:', error);
      return { events: [] };
    }
  },

  async analyzeEconomicEvent(event) {
    try {
      const response = await axios.post(`${API}/economic-event/analyze`, event, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error analyzing economic event:', error);
      return { analysis: null, error: error.message };
    }
  },

  async getCongressTrades(limit = 100) {
    try {
      const response = await axios.get(`${API}/investors/congress`, { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('Error fetching congress trades:', error);
      return [];
    }
  },

  async getFundHoldings(ticker) {
    try {
      const response = await axios.get(`${API}/investors/fund-holdings`, { params: { ticker } });
      return response.data;
    } catch (error) {
      console.error('Error fetching fund holdings:', error);
      return [];
    }
  }
};

// News & Insider API
export const newsAPI = {
  async getMarketNews(apiKey, limit = 20) {
    try {
      const response = await axios.get(`${API}/news/market`, { params: { api_key: apiKey, limit } });
      return response.data;
    } catch (error) {
      console.error('Error fetching market news:', error);
      return [];
    }
  },

  async getCompanyNews(ticker, apiKey) {
    try {
      const response = await axios.get(`${API}/news/company/${ticker}`, { params: { api_key: apiKey } });
      return response.data;
    } catch (error) {
      console.error('Error fetching company news:', error);
      return [];
    }
  },

  async getInsiderTrading(ticker, apiKey) {
    try {
      const response = await axios.get(`${API}/insider/${ticker}`, { params: { api_key: apiKey } });
      return response.data;
    } catch (error) {
      console.error('Error fetching insider trading:', error);
      return [];
    }
  },

  async getBulkInsider(tickers, apiKey) {
    try {
      const response = await axios.get(`${API}/insider/bulk`, { params: { tickers: tickers.join(","), api_key: apiKey } });
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk insider:', error);
      return [];
    }
  }
};

// AI Advisor API
export const aiAPI = {
  async analyze(prompt, portfolioContext, sessionId = "default") {
    try {
      const response = await axios.post(`${API}/ai/analyze`, {
        prompt,
        portfolio_context: portfolioContext,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Error calling AI:', error);
      throw error;
    }
  },

  async expertChat(prompt, portfolioContext = "", sessionId = "", images = []) {
    try {
      const response = await axios.post(`${API}/expert/chat`, {
        prompt,
        portfolio_context: portfolioContext,
        session_id: sessionId,
        images
      });
      return response.data;
    } catch (error) {
      console.error('Expert chat error:', error);
      throw error;
    }
  },

  async expertDebate(originalPrompt, expertResponses, portfolioContext = "", sessionId = "") {
    try {
      const response = await axios.post(`${API}/expert/debate`, {
        original_prompt: originalPrompt,
        expert_responses: expertResponses,
        portfolio_context: portfolioContext,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Expert debate error:', error);
      throw error;
    }
  },

  async expertSynthesis(originalPrompt, round1Experts, round2Experts, round1Consensus, round2Consensus, portfolioContext = "") {
    try {
      const response = await axios.post(`${API}/expert/synthesis`, {
        original_prompt: originalPrompt,
        round1_experts: round1Experts,
        round2_experts: round2Experts,
        round1_consensus: round1Consensus,
        round2_consensus: round2Consensus,
        portfolio_context: portfolioContext
      });
      return response.data;
    } catch (error) {
      console.error('Expert synthesis error:', error);
      throw error;
    }
  },

  async saveConversation(owner, prompt, round1, round2, round3, followupMessages = []) {
    try {
      const response = await axios.post(`${API}/expert/conversation/save`, {
        owner, prompt, round1, round2, round3, followup_messages: followupMessages
      });
      return response.data;
    } catch (error) {
      console.error('Save conversation error:', error);
      return null;
    }
  },

  async getConversations(owner = "Helmi", limit = 20) {
    try {
      const response = await axios.get(`${API}/expert/conversations`, { params: { owner, limit } });
      return response.data;
    } catch (error) {
      console.error('Get conversations error:', error);
      return [];
    }
  },

  async expertFollowup(question, round1, round2, round3, followupMessages, portfolioContext, originalPrompt) {
    try {
      const response = await axios.post(`${API}/expert/followup`, {
        question, round1, round2, round3,
        followup_messages: followupMessages,
        portfolio_context: portfolioContext,
        original_prompt: originalPrompt
      });
      return response.data;
    } catch (error) {
      console.error('Expert followup error:', error);
      throw error;
    }
  }
};

// Algo Trading API
export const algoAPI = {
  // Full technical analysis for a ticker
  async analyze(ticker, apiKey) {
    try {
      const response = await axios.post(`${API}/algo/analyze`, {
        ticker,
        api_key: apiKey
      });
      return response.data;
    } catch (error) {
      console.error(`Error in algo analysis for ${ticker}:`, error);
      throw error;
    }
  },

  async getChart(ticker, timeframe = "3M") {
    try {
      const response = await axios.post(`${API}/algo/chart`, { ticker, timeframe });
      return response.data;
    } catch (error) {
      console.error(`Error fetching chart for ${ticker}:`, error);
      return null;
    }
  },

  // Run backtest
  async backtest(ticker, apiKey, strategy = "RSI_MEAN_REVERSION", initialCapital = 10000) {
    try {
      const response = await axios.post(`${API}/algo/backtest`, {
        ticker,
        api_key: apiKey,
        strategy,
        initial_capital: initialCapital
      });
      return response.data;
    } catch (error) {
      console.error(`Error in backtest for ${ticker}:`, error);
      throw error;
    }
  },

  // Portfolio optimization
  async optimize(positions, prices, targetAllocation = "BALANCED") {
    try {
      const response = await axios.post(`${API}/algo/optimize`, {
        positions,
        prices,
        target_allocation: targetAllocation
      });
      return response.data;
    } catch (error) {
      console.error('Error in portfolio optimization:', error);
      throw error;
    }
  },

  // Position size calculator
  async calculatePositionSize(portfolioValue, riskPerTrade, entryPrice, stopLoss) {
    try {
      const response = await axios.post(`${API}/algo/position-size`, {
        portfolio_value: portfolioValue,
        risk_per_trade: riskPerTrade,
        entry_price: entryPrice,
        stop_loss: stopLoss
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating position size:', error);
      throw error;
    }
  },

  // Scan multiple tickers
  async scan(tickers, apiKey) {
    try {
      const response = await axios.post(`${API}/algo/scan`, null, {
        params: { tickers: tickers.join(','), api_key: apiKey }
      });
      return response.data;
    } catch (error) {
      console.error('Error in algo scan:', error);
      throw error;
    }
  },

  async getSavedScan() {
    try {
      const response = await axios.get(`${API}/scan/saved`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      return { results: [], saved_at: null };
    }
  },

  async saveScanResults(results) {
    try {
      await axios.post(`${API}/scan/save`, { results }, { timeout: 10000 });
    } catch (error) {
      console.error('Error saving scan:', error);
    }
  },

  async getBacktestConclusion(ticker, backtestResult, strategy, stockFundamentals, algoAnalysis, stockPerf) {
    try {
      const payload = {
        ticker,
        backtest: backtestResult ? {
          total_return: backtestResult.total_return_pct,
          win_rate: backtestResult.win_rate_pct,
          max_drawdown: backtestResult.max_drawdown_pct,
          total_trades: backtestResult.total_trades,
          strategy,
        } : {},
        kpis: stockFundamentals?.kpis || {},
        analysis: algoAnalysis?.analysis || {},
        performance: stockPerf || {},
        sector: algoAnalysis?.sector_info?.sector || stockFundamentals?.sector || "N/A",
        industry: algoAnalysis?.sector_info?.industry || stockFundamentals?.industry || "N/A",
      };
      const response = await axios.post(`${API}/algo/backtest-conclusion`, payload, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error getting backtest conclusion:', error);
      return { conclusion: "Erreur lors de l'analyse" };
    }
  },

  async saveAnalysis(owner, analysis) {
    try {
      const response = await axios.post(`${API}/algo/save-analysis`, { owner, analysis });
      return response.data;
    } catch (error) {
      console.error('Error saving algo analysis:', error);
    }
  },

  async getHistory(owner) {
    try {
      const response = await axios.get(`${API}/algo/history/${owner}`);
      return response.data;
    } catch (error) {
      console.error('Error loading algo history:', error);
      return [];
    }
  },

  async getHistoryTicker(owner, ticker) {
    try {
      const response = await axios.get(`${API}/algo/history/${owner}/${ticker}`);
      return response.data;
    } catch (error) {
      console.error('Error loading algo history for ticker:', error);
      return null;
    }
  },

  async getTopRated(owner) {
    try {
      const response = await axios.get(`${API}/algo/top-rated/${owner}`);
      return response.data;
    } catch (error) {
      console.error('Error loading top rated:', error);
      return { top_rated: [], total_analyzed: 0 };
    }
  }
};

// Top Picks API (context-specific)
export const topPicksAPI = {
  async getDaily() {
    try {
      const response = await axios.get(`${API}/top-picks/daily`);
      return response.data;
    } catch (error) {
      console.error('Error loading daily top picks:', error);
      return { top_rated: [], source: "daily" };
    }
  },
  async getScanner(owner) {
    try {
      const response = await axios.get(`${API}/top-picks/scanner/${encodeURIComponent(owner)}`);
      return response.data;
    } catch (error) {
      console.error('Error loading scanner top picks:', error);
      return { top_rated: [], source: "scanner" };
    }
  },
  async getHistory(days = 14) {
    try {
      const response = await axios.get(`${API}/top-picks/history?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error loading top picks history:', error);
      return { history: [] };
    }
  },
  async saveSnapshot() {
    try {
      const response = await axios.post(`${API}/top-picks/save-snapshot`);
      return response.data;
    } catch (error) {
      console.error('Error saving snapshot:', error);
      return { status: "error" };
    }
  },
};

// Institutional moves API
export const institutionalAPI = {
  async getTopMoves() {
    try {
      const response = await axios.get(`${API}/investors/top-moves`, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('Error loading institutional moves:', error);
      return { top_moves: [], alerts: [] };
    }
  },
  async getAlerts() {
    try {
      const response = await axios.get(`${API}/investors/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error loading institutional alerts:', error);
      return { alerts: [], count: 0 };
    }
  },
  async markAlertsRead() {
    try {
      await axios.post(`${API}/investors/alerts/mark-read`);
    } catch (error) {
      console.error('Error marking alerts read:', error);
    }
  },
};

// Confluence screener API
export const confluenceAPI = {
  async getScreener() {
    try {
      const response = await axios.get(`${API}/confluence/screener`, { timeout: 30000 });
      return response.data;
    } catch (error) {
      console.error('Error loading confluence screener:', error);
      return { signals: [] };
    }
  },
};

// Daily AI briefing API
export const briefingDailyAPI = {
  async get(owner, force = false) {
    try {
      const params = force ? '?force=true' : '';
      // 2026-06-17 — 55s timeout: fails fast just before LB 502 (60s). DailyBriefing
      // polls every 25s for cache fill after a force refresh.
      const response = await axios.get(`${API}/briefing/daily/${encodeURIComponent(owner)}${params}`, { timeout: 55000 });
      return response.data;
    } catch (error) {
      console.error('Error loading daily briefing:', error);
      return { briefing: "" };
    }
  },
};

// Portfolio API
export const portfolioAPI = {
  async getPositions(owner) {
    try {
      const response = await axios.get(`${API}/portfolio/${owner}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      return [];
    }
  },

  async getAllPositions() {
    try {
      const response = await axios.get(`${API}/portfolio`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all portfolio:', error);
      return [];
    }
  },

  async addPosition(position) {
    try {
      const response = await axios.post(`${API}/portfolio/add`, position);
      return response.data;
    } catch (error) {
      console.error('Error adding position:', error);
      throw error;
    }
  },

  async updatePosition(positionId, updates) {
    try {
      const response = await axios.put(`${API}/portfolio/position/${positionId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating position:', error);
      throw error;
    }
  },

  async deletePosition(positionId, sellPrice) {
    try {
      const params = sellPrice ? `?sell_price=${sellPrice}` : '';
      const response = await axios.delete(`${API}/portfolio/position/${positionId}${params}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting position:', error);
      throw error;
    }
  },

  async command(text, owner) {
    try {
      const response = await axios.post(`${API}/portfolio/command`, { command: text, owner });
      return response.data;
    } catch (error) {
      console.error('Error executing command:', error);
      throw error;
    }
  },

  async initPortfolio(positions) {
    try {
      const response = await axios.post(`${API}/portfolio/init`, positions);
      return response.data;
    } catch (error) {
      console.error('Error initializing portfolio:', error);
      throw error;
    }
  },

  async getSellSignals(owner) {
    try {
      const response = await axios.get(`${API}/portfolio/sell-signals/${encodeURIComponent(owner)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sell signals:', error);
      return { signals: {} };
    }
  },

  async getRealizedPnl(owner) {
    try {
      const response = await axios.get(`${API}/portfolio/realized-pnl/${encodeURIComponent(owner)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching realized PnL:', error);
      return { total_realized_pnl: 0, transaction_count: 0, recent_transactions: [], wins: 0, losses: 0, win_rate: 0 };
    }
  },

  async initDefaultPortfolio() {
    try {
      const response = await axios.post(`${API}/portfolio/init-default`);
      return response.data;
    } catch (error) {
      console.error('Error initializing default portfolio:', error);
      throw error;
    }
  },

  async autoClassifyStrategies() {
    try {
      const response = await axios.post(`${API}/portfolio/auto-classify`);
      return response.data;
    } catch (error) {
      console.error('Error auto-classifying strategies:', error);
      throw error;
    }
  }
};

// Market Status API
export const marketAPI = {
  async getStatus() {
    try {
      const response = await axios.get(`${API}/market/status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching market status:', error);
      return {};
    }
  },

  async getTickerStatus(ticker) {
    try {
      const response = await axios.get(`${API}/market/status/${ticker}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticker status:', error);
      return null;
    }
  },

  async getExchangeRates() {
    try {
      const response = await axios.get(`${API}/exchange-rates`, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      return { rates: { USD: 1.0, EUR: 1.08, GBP: 1.27, CHF: 1.12, NOK: 0.094, SEK: 0.096, DKK: 0.145 }, base: "USD" };
    }
  },

  async getOpportunities() {
    try {
      const response = await axios.get(`${API}/market/opportunities`, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      return { opportunities: [] };
    }
  }
};

// Alerts API
export const alertsAPI = {
  async getAlerts() {
    try {
      const response = await axios.get(`${API}/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }
  },

  async addAlert(alert) {
    try {
      const response = await axios.post(`${API}/alerts`, alert);
      return response.data;
    } catch (error) {
      console.error('Error adding alert:', error);
      throw error;
    }
  },

  async clearAlerts() {
    try {
      const response = await axios.delete(`${API}/alerts`);
      return response.data;
    } catch (error) {
      console.error('Error clearing alerts:', error);
      throw error;
    }
  }
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API}/health`);
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    return null;
  }
};

// Performance API
export const performanceAPI = {
  async saveSnapshot(positions, prices, totalValue, totalCost) {
    try {
      const response = await axios.post(`${API}/performance/snapshot`, {
        positions, prices, total_value: totalValue, total_cost: totalCost
      });
      return response.data;
    } catch (error) {
      console.error('Error saving snapshot:', error);
      return null;
    }
  },

  async getSnapshots(days = 90) {
    try {
      const response = await axios.get(`${API}/performance/snapshots`, { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching snapshots:', error);
      return [];
    }
  },

  async getSummary() {
    try {
      const response = await axios.get(`${API}/performance/summary`);
      return response.data;
    } catch (error) {
      console.error('Error fetching performance summary:', error);
      return null;
    }
  }
};


// Briefing API
export const briefingAPI = {
  async generate(owner, apiKey, email = "", positions = [], prices = {}) {
    try {
      const response = await axios.post(`${API}/briefing/generate`, {
        owner, api_key: apiKey, email, positions, prices
      });
      return response.data;
    } catch (error) {
      console.error('Error generating briefing:', error);
      throw error;
    }
  },

  async sendEmail(owner, apiKey, email, positions = [], prices = {}) {
    try {
      const response = await axios.post(`${API}/briefing/send-email`, {
        owner, api_key: apiKey, email, positions, prices
      });
      return response.data;
    } catch (error) {
      console.error('Error sending briefing email:', error);
      throw error;
    }
  },

  async getHistory(owner, limit = 10) {
    try {
      const response = await axios.get(`${API}/briefing/history`, {
        params: { owner, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching briefing history:', error);
      return [];
    }
  }
};

// Watchlist API
export const watchlistAPI = {
  async get(owner) {
    try {
      const response = await axios.get(`${API}/watchlist/${encodeURIComponent(owner)}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      return { owner, stocks: [], generated_at: null };
    }
  },

  async generate(owner, apiKey = "") {
    const response = await axios.post(`${API}/watchlist/generate/${encodeURIComponent(owner)}`, 
      { api_key: apiKey },
      { headers: getAuthHeaders(), timeout: 600000 }
    );
    return response.data;
  },

  async addStock(owner, ticker, name, sector, region, catalyst = "") {
    const response = await axios.post(`${API}/watchlist/${encodeURIComponent(owner)}/add`, 
      { ticker, name, sector, region, catalyst },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async removeStock(owner, ticker) {
    const response = await axios.delete(`${API}/watchlist/${encodeURIComponent(owner)}/${encodeURIComponent(ticker)}`, 
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async importCSV(owner, csvData, replace = false) {
    const response = await axios.post(`${API}/watchlist/import-csv/${encodeURIComponent(owner)}`, 
      { csv_data: csvData, replace },
      { headers: getAuthHeaders(), timeout: 60000 }
    );
    return response.data;
  },

  async scanOpportunities(owner) {
    const response = await axios.post(`${API}/watchlist/scan-opportunities/${encodeURIComponent(owner)}`,
      {},
      { headers: getAuthHeaders(), timeout: 120000 }
    );
    return response.data;
  },

  async getAlerts(owner) {
    const response = await axios.get(`${API}/watchlist/alerts/${encodeURIComponent(owner)}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async getTopPicks(owner) {
    const response = await axios.get(`${API}/watchlist/top-picks/${encodeURIComponent(owner)}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async analyzeTopPicksAI(owner) {
    const response = await axios.post(`${API}/watchlist/top-picks-ai/${encodeURIComponent(owner)}`,
      {},
      { headers: getAuthHeaders(), timeout: 90000 }
    );
    return response.data;
  },

  async getMarketTiming() {
    const response = await axios.get(`${API}/market-timing`,
      { headers: getAuthHeaders(), timeout: 60000 }
    );
    return response.data;
  },

  async getMarketTimingHistory() {
    try {
      const response = await axios.get(`${API}/market-timing/history`,
        { headers: getAuthHeaders(), timeout: 10000 }
      );
      return response.data;
    } catch (e) {
      return { history: [] };
    }
  },

  async getSectorHeatmap(owner, force = false) {
    const url = `${API}/sector-heatmap/${encodeURIComponent(owner)}${force ? '?force=true' : ''}`;
    const response = await axios.get(url,
      { headers: getAuthHeaders(), timeout: force ? 120000 : 60000 }
    );
    return response.data;
  },

  async getSectorStocks(sectorKey, owner) {
    const response = await axios.get(`${API}/sector-stocks/${encodeURIComponent(sectorKey)}/${encodeURIComponent(owner)}`,
      { headers: getAuthHeaders(), timeout: 60000 }
    );
    return response.data;
  }
};

// 13F SEC EDGAR API
export const investorsAPI = {
  async get13FHoldings(fundKey) {
    try {
      const response = await axios.get(`${API}/investors/13f/${encodeURIComponent(fundKey)}`, { timeout: 45000 });
      return response.data;
    } catch (error) {
      console.error(`Error fetching 13F for ${fundKey}:`, error);
      return { error: error.message, holdings: [] };
    }
  },

  async getAll13F() {
    try {
      const response = await axios.get(`${API}/investors/13f-all`, { timeout: 300000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching all 13F data:', error);
      return { funds: {}, consensus: [], error: error.message };
    }
  },

  async trackTicker(ticker) {
    try {
      const response = await axios.get(`${API}/investors/track/${encodeURIComponent(ticker)}`, { timeout: 30000 });
      return response.data;
    } catch (error) {
      console.error(`Error tracking ${ticker}:`, error);
      return { ticker, found_in_funds: 0, holdings: [] };
    }
  }
};

// Earnings Calendar API
export const earningsAPI = {
  async getSaved() {
    try {
      const response = await axios.get(`${API}/earnings/saved`, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved earnings:', error);
      return { earnings_data: null, ai_signals: {}, batch_result: null };
    }
  },

  async getWeekly() {
    try {
      const response = await axios.get(`${API}/earnings/weekly`, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching weekly earnings:', error);
      return { earnings: [], total: 0 };
    }
  },

  async getAISignal(ticker, earningsData) {
    try {
      const response = await axios.post(`${API}/earnings/ai-signal`, {
        ticker,
        earnings_data: earningsData
      }, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error(`Error fetching AI signal for ${ticker}:`, error);
      return { ticker, ai_signal: { signal: "ATTENDRE", conviction: 3, raison: "Erreur" } };
    }
  },

  async getAISignalBatch(earningsList) {
    try {
      const response = await axios.post(`${API}/earnings/ai-signal-batch`, {
        earnings: earningsList
      }, { timeout: 120000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching batch AI signals:', error);
      return { signals: {}, ranking: [], best_play: null, summary: "Erreur" };
    }
  }
};

// Track Record API
export const trackRecordAPI = {
  async get(owner) {
    try {
      const response = await axios.get(`${API}/track-record/${encodeURIComponent(owner)}`, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching track record:', error);
      return { signals: [], stats: { total: 0, wins: 0, losses: 0, open: 0, win_rate: 0, avg_pnl: 0 } };
    }
  }
};


// Trading 212 API
export const trading212API = {
  async getPortfolio(owner) {
    const response = await axios.get(`${API}/trading212/portfolio`, { params: { owner }, timeout: 20000 });
    return response.data;
  },
  async getAccount(owner) {
    const response = await axios.get(`${API}/trading212/account`, { params: { owner }, timeout: 10000 });
    return response.data;
  },
  async sync(owner) {
    const response = await axios.post(`${API}/trading212/sync`, { owner }, { timeout: 60000 });
    return response.data;
  },
};

export const battleAPI = {
  async getStatus() {
    try {
      const response = await axios.get(`${API}/battle/status`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      return { active: false, traders: {} };
    }
  },
  async start() {
    const response = await axios.post(`${API}/battle/start`, {}, { timeout: 10000 });
    return response.data;
  },
  async runRound() {
    const response = await axios.post(`${API}/battle/round`, {}, { timeout: 120000 });
    return response.data;
  },
  async getTopTrades() {
    try {
      const response = await axios.get(`${API}/battle/top-trades`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      return { trades: [] };
    }
  },
};

export const massiveScanAPI = {
  async getResults() {
    try {
      const response = await axios.get(`${API}/scan/massive`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      return { results: [], total: 0 };
    }
  },
  async triggerScan() {
    try {
      const response = await axios.post(`${API}/scan/massive/run`, {}, { timeout: 180000 });
      return response.data;
    } catch (error) {
      return { ok: false, error: "Timeout" };
    }
  },
  async getAlertHistory() {
    try {
      const response = await axios.get(`${API}/alerts/history`, { timeout: 30000 });
      return response.data;
    } catch (error) {
      return { alerts: [] };
    }
  },
};

// ==================== PRICE ALERTS API ====================
export const priceAlertsAPI = {
  async create(alert) {
    const response = await axios.post(`${API}/price-alerts`, alert, { timeout: 10000 });
    return response.data;
  },
  async getActive(owner) {
    const response = await axios.get(`${API}/price-alerts/${encodeURIComponent(owner)}`, { timeout: 10000 });
    return response.data;
  },
  async getTriggered(owner) {
    const response = await axios.get(`${API}/price-alerts/triggered/${encodeURIComponent(owner)}`, { timeout: 10000 });
    return response.data;
  },
  async getAll(owner) {
    const response = await axios.get(`${API}/price-alerts/all/${encodeURIComponent(owner)}`, { timeout: 10000 });
    return response.data;
  },
  async dismiss(ticker, owner, created_at) {
    const response = await axios.post(`${API}/price-alerts/dismiss`, { ticker, owner, created_at }, { timeout: 10000 });
    return response.data;
  },
  async remove(owner, ticker) {
    const response = await axios.delete(`${API}/price-alerts/${encodeURIComponent(owner)}/${encodeURIComponent(ticker)}`, { timeout: 10000 });
    return response.data;
  },
  async getEnriched(owner) {
    const response = await axios.get(`${API}/price-alerts/enriched/${encodeURIComponent(owner)}`, { timeout: 15000 });
    return response.data;
  },
  async getHot(owner) {
    const response = await axios.get(`${API}/alerts/hot/${encodeURIComponent(owner)}`, { timeout: 15000 });
    return response.data;
  },
};


export const capitalFlowAPI = {
  async getData(finnhubKey = "") {
    const params = finnhubKey ? { finnhub_key: finnhubKey } : {};
    const response = await axios.get(`${API}/capital-flow`, { params, timeout: 30000 });
    return response.data;
  },
  async getAIReport() {
    const response = await axios.post(`${API}/capital-flow/ai-report`, {}, { timeout: 60000 });
    return response.data;
  },
};

export const opportunitiesAPI = {
  async getRanked() {
    const response = await axios.get(`${API}/opportunities`, { headers: getAuthHeaders(), timeout: 30000 });
    return response.data;
  },
};


export const botAnalyticsAPI = {
  async getSummary() {
    const response = await axios.get(`${API}/bot/analytics-summary`, { headers: getAuthHeaders(), timeout: 30000 });
    return response.data;
  },
  async getDiagnostics(limit = 30) {
    const response = await axios.get(`${API}/bot/diagnostics?limit=${limit}`, { headers: getAuthHeaders(), timeout: 15000 });
    return response.data;
  },
  async getDailyRecap(date) {
    const url = date ? `${API}/bot/daily-recap?date=${date}` : `${API}/bot/daily-recap`;
    const response = await axios.get(url, { headers: getAuthHeaders(), timeout: 60000 });
    return response.data;
  },
  async getRecapHistory(days = 7) {
    const response = await axios.get(`${API}/bot/daily-recap/history?days=${days}`, { headers: getAuthHeaders(), timeout: 15000 });
    return response.data;
  },
  async getGptVsClaude() {
    const response = await axios.get(`${API}/bot/analytics/gpt-vs-claude`, { headers: getAuthHeaders(), timeout: 15000 });
    return response.data;
  },
  async getLiveDiscoveries() {
    const response = await axios.get(`${API}/bot/live-discoveries`, { headers: getAuthHeaders(), timeout: 15000 });
    return response.data;
  },
  async getAutopilot() {
    const response = await axios.get(`${API}/bot/autopilot`, { headers: getAuthHeaders(), timeout: 15000 });
    return response.data;
  },
  async triggerAutopilot() {
    const response = await axios.post(`${API}/bot/autopilot/run`, {}, { headers: getAuthHeaders(), timeout: 30000 });
    return response.data;
  },
};


// Gems API — Hidden Gems / Future 10x Stocks
export const gemsAPI = {
  async getTopGems(limit = 30) {
    const response = await axios.get(`${API}/gems/top`, {
      params: { limit },
      headers: getAuthHeaders(),
      timeout: 120000,
    });
    return response.data;
  },
  async getSectors() {
    const response = await axios.get(`${API}/gems/sectors`, { headers: getAuthHeaders() });
    return response.data;
  },
  async getDetail(ticker) {
    const response = await axios.get(`${API}/gems/detail/${ticker}`, {
      headers: getAuthHeaders(),
      timeout: 30000,
    });
    return response.data;
  },
  async getThesis(ticker) {
    const response = await axios.post(`${API}/gems/thesis/${ticker}`, {}, {
      headers: getAuthHeaders(),
      timeout: 60000,
    });
    return response.data;
  },
  async triggerScan() {
    const response = await axios.post(`${API}/gems/scan`, {}, {
      headers: getAuthHeaders(),
      timeout: 180000,
    });
    return response.data;
  },
  async filterGems(filters = {}) {
    const response = await axios.get(`${API}/gems/filter`, {
      params: filters,
      headers: getAuthHeaders(),
      timeout: 30000,
    });
    return response.data;
  },
  async getInsiderAlerts() {
    const response = await axios.get(`${API}/gems/insider-alerts`, {
      headers: getAuthHeaders(),
      timeout: 120000,
    });
    return response.data;
  },
  async getWatchlist(owner) {
    const response = await axios.get(`${API}/gems/watchlist/${encodeURIComponent(owner)}`, {
      headers: getAuthHeaders(),
      timeout: 30000,
    });
    return response.data;
  },
  async addToWatchlist(ticker, owner) {
    const response = await axios.post(`${API}/gems/watchlist`, { ticker, owner }, {
      headers: getAuthHeaders(),
      timeout: 15000,
    });
    return response.data;
  },
  async removeFromWatchlist(owner, ticker) {
    const response = await axios.delete(`${API}/gems/watchlist/${encodeURIComponent(owner)}/${encodeURIComponent(ticker)}`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  },
  async getHallOfFame() {
    const response = await axios.get(`${API}/gems/hall-of-fame`, {
      headers: getAuthHeaders(),
      timeout: 30000,
    });
    return response.data;
  },
};

export const convictionAPI = {
  async getScores(owner) {
    const response = await axios.get(`${API}/conviction-scores/${owner}`, {
      headers: getAuthHeaders(),
      timeout: 120000,
    });
    return response.data;
  },
};

// ---------- Disciplined Scan Bot ----------
export const disciplinedScanAPI = {
  async analyze({ owner = "Helmi", scan_type = "top_movers", tickers = null, max_tickers = 8 }) {
    const body = { owner, scan_type, max_tickers };
    if (tickers && tickers.length) body.tickers = tickers;
    const response = await axios.post(`${API}/disciplined-scan/analyze`, body, {
      headers: getAuthHeaders(),
      timeout: 240000,
    });
    return response.data;
  },
  async getHistory(owner, ticker = null, limit = 50) {
    const params = { limit };
    if (ticker) params.ticker = ticker;
    const response = await axios.get(`${API}/disciplined-scan/history/${encodeURIComponent(owner)}`, {
      params,
      headers: getAuthHeaders(),
      timeout: 30000,
    });
    return response.data;
  },
  async getWatchlist(owner) {
    const response = await axios.get(`${API}/disciplined-scan/watchlist/${encodeURIComponent(owner)}`, {
      headers: getAuthHeaders(),
      timeout: 15000,
    });
    return response.data;
  },
  async setWatchlist(owner, tickers) {
    const response = await axios.post(`${API}/disciplined-scan/watchlist/${encodeURIComponent(owner)}`,
      { tickers },
      { headers: getAuthHeaders(), timeout: 15000 }
    );
    return response.data;
  },
};
